import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { TenantProvider } from "@/contexts/TenantContext";
import { AuthPage } from "@/components/auth/AuthPage";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PasswordResetPage } from "@/components/auth/PasswordResetPage";
import { UpdatePasswordPage } from "@/components/auth/UpdatePasswordPage";
import { PasswordSetupPage } from "@/components/auth/PasswordSetupPage";
import { Layout } from "@/components/layout/Layout";
import { Dashboard } from "@/components/dashboard/Dashboard"; // Keep dashboard non-lazy for fast initial load
import { CalendarSkeleton, TableSkeleton, ServicesSkeleton, FormSkeleton } from "@/components/ui/skeletons";
import { shouldRetryQuery } from "@/lib/queryRetry";

// Lazy load non-critical route components
const CalendarView = lazy(() => import("@/components/calendar/CalendarView"));
const ConversationsOverview = lazy(() => import("@/components/conversations/ConversationsOverview"));
const Services = lazy(() => import("@/components/services/Services"));
const ServiceForm = lazy(() => import("@/components/services/ServiceForm"));
const TeamMemberForm = lazy(() => import("@/components/team/TeamMemberForm"));
const Customers = lazy(() => import("@/components/customers/Customers"));
const Tasks = lazy(() => import("@/components/tasks/Tasks"));
const AvailabilityModule = lazy(() => import("@/components/availability/AvailabilityModule"));
const SettingsOverview = lazy(() => import("@/components/settings/SettingsOverview"));
const Analytics = lazy(() => import("@/components/analytics/Analytics"));
const ReferralProgram = lazy(() => import("@/components/referral/ReferralProgram"));
const AdminConsole = lazy(() => import("@/components/admin/AdminConsole"));
const MarketingConsentVerification = lazy(() => import("@/components/public/MarketingConsentVerification").then(module => ({ default: module.MarketingConsentVerification })));
const CustomerAppointmentManagement = lazy(() => import("@/components/public/CustomerAppointmentManagement").then(module => ({ default: module.CustomerAppointmentManagement })));
const CallForwardingPage = lazy(() => import("./pages/CallForwardingPage").then(module => ({ default: module.CallForwardingPage })));
const GBPOAuthCallback = lazy(() => import("./pages/GBPOAuthCallback"));
const GBPReviewApproval = lazy(() => import("@/components/public/GBPReviewApproval").then(module => ({ default: module.GBPReviewApproval })));
const NotFound = lazy(() => import("./pages/NotFound"));

// Optimized QueryClient configuration for better caching and performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes (v5: gcTime)
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnReconnect: true, // Refetch when connection is restored
      // Wiederholt nicht mehr blind. Ein 15s-Timeout der Datenbank wird sofort
      // als Fehler gemeldet, statt zweimal nachzuschlagen und dabei je weitere
      // 15 Sekunden eine Verbindung zu belegen. Siehe lib/queryRetry.ts.
      retry: shouldRetryQuery,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    },
    mutations: {
      retry: false, // Don't retry mutations by default
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public routes - these don't need TenantProvider */}
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth/reset-password" element={<PasswordResetPage />} />
            <Route path="/auth/update-password" element={<UpdatePasswordPage />} />
            <Route path="/auth/confirm" element={<UpdatePasswordPage />} />
            <Route path="/auth/setup-password" element={<PasswordSetupPage />} />
            <Route path="/verify-marketing-consent" element={
              <Suspense fallback={<div>Loading...</div>}>
                <MarketingConsentVerification />
              </Suspense>
            } />
            <Route path="/manage-appointment" element={
              <Suspense fallback={<div>Loading...</div>}>
                <CustomerAppointmentManagement />
              </Suspense>
            } />
            <Route path="/oauth/gbp/callback" element={
              <Suspense fallback={<div>Loading...</div>}>
                <GBPOAuthCallback />
              </Suspense>
            } />
            <Route path="/gbp/review" element={
              <Suspense fallback={<div>Loading...</div>}>
                <GBPReviewApproval />
              </Suspense>
            } />

            {/* Routes that need TenantProvider - wrap them individually */}
            <Route path="/*" element={
              <TenantProvider>
                <Routes>

                  {/* Public booking flow removed (moved to customer-portal) */}

                  {/* Protected routes */}
                  <Route path="/" element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<Dashboard />} />
                    <Route path="dashboard" element={<Dashboard />} />

                    {/* Service Management Routes */}
                    <Route path="services" element={
                      <Suspense fallback={<ServicesSkeleton />}>
                        <Services />
                      </Suspense>
                    } />
                    <Route path="services/new" element={
                      <Suspense fallback={<FormSkeleton />}>
                        <ServiceForm />
                      </Suspense>
                    } />
                    <Route path="services/edit/:id" element={
                      <Suspense fallback={<FormSkeleton />}>
                        <ServiceForm />
                      </Suspense>
                    } />

                    {/* Team Management Form Routes */}
                    <Route path="team/new" element={
                      <Suspense fallback={<FormSkeleton />}>
                        <TeamMemberForm />
                      </Suspense>
                    } />
                    <Route path="team/edit/:id" element={
                      <Suspense fallback={<FormSkeleton />}>
                        <TeamMemberForm />
                      </Suspense>
                    } />

                    {/* Availability Management Routes */}
                    <Route path="availability" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <AvailabilityModule />
                      </Suspense>
                    } />
                    <Route path="availability/:category" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <AvailabilityModule />
                      </Suspense>
                    } />

                    {/* Calendar Routes */}
                    <Route path="calendar" element={
                      <Suspense fallback={<CalendarSkeleton />}>
                        <CalendarView />
                      </Suspense>
                    } />

                    {/* Conversations Routes */}
                    <Route path="conversations" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <ConversationsOverview />
                      </Suspense>
                    } />
                    <Route path="conversations/customer/:customerId" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <ConversationsOverview />
                      </Suspense>
                    } />
                    <Route path="conversations/customer/:customerId/transcript/:conversationId" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <ConversationsOverview />
                      </Suspense>
                    } />
                    <Route path="conversations/call-forwarding" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <CallForwardingPage />
                      </Suspense>
                    } />

                    {/* Customer Routes */}
                    <Route path="customers" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <Customers />
                      </Suspense>
                    } />
                    <Route path="customers/:customerId" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <Customers />
                      </Suspense>
                    } />
                    <Route path="customers/new" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <Customers />
                      </Suspense>
                    } />

                    {/* Tasks Routes */}
                    <Route path="tasks" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <Tasks />
                      </Suspense>
                    } />

                    {/* Settings Routes */}
                    <Route path="settings" element={
                      <Suspense fallback={<FormSkeleton />}>
                        <SettingsOverview />
                      </Suspense>
                    } />
                    <Route path="settings/:category" element={
                      <Suspense fallback={<FormSkeleton />}>
                        <SettingsOverview />
                      </Suspense>
                    } />

                    {/* Internal admin console. The route is guarded in the
                        database too: its RPCs refuse anyone who is not Sylva
                        staff, so reaching this URL on its own gains nothing. */}
                    <Route path="admin" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <AdminConsole />
                      </Suspense>
                    } />

                    {/* Referral Program */}
                    <Route path="referrals" element={
                      <Suspense fallback={<FormSkeleton />}>
                        <ReferralProgram />
                      </Suspense>
                    } />

                    {/* Analytics Routes */}
                    <Route path="analytics" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <Analytics />
                      </Suspense>
                    } />
                    <Route path="analytics/:category" element={
                      <Suspense fallback={<TableSkeleton />}>
                        <Analytics />
                      </Suspense>
                    } />

                  </Route>

                  {/* Catch-all route */}
                  <Route path="*" element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <NotFound />
                    </Suspense>
                  } />
                </Routes>
              </TenantProvider>
            } />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
